import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { Select, Input, Cascader } from 'antd'
import { debounce } from 'lodash'
import styled from 'styled-components'
import { t } from '@/i18n'

/**
 * 搜索选项接口
 */
export interface SearchOption {
  value: string
  label: string
}

/**
 * 级联选择器选项接口
 */
export interface CascaderOption {
  value: string | number
  label: string | React.ReactElement
  children?: CascaderOption[]
}

/**
 * 支持的组件类型
 */
export type ComponentType = 'input' | 'select' | 'cascader' | 'custom'

/**
 * Select组件的选择模式
 */
export type SelectMode = 'single' | 'multiple'

/**
 * 级联选择器配置接口
 */
export interface CascaderConfig {
  /** 级联的最大层级数 */
  maxLevel?: number
  /** 当路径长度等于此值时，取该节点下所有子项的ID */
  expandAtLevel?: number
  /** 是否保留完整路径（不进行处理，直接返回原始路径数组） */
  keepFullPath?: boolean
}

/**
 * 搜索项配置接口
 * @param key - 搜索项的唯一标识
 * @param label - 搜索项显示名称
 * @param componentType - 组件类型
 * @param placeholder - 占位符文本
 * @param options - 静态选项数据（用于select和cascader）
 * @param getOptions - 动态获取选项数据的函数（优先级高于options）
 * @param selectMode - Select组件的选择模式（single/multiple），默认为multiple
 * @param props - 额外的组件属性
 * @param render - 自定义渲染函数（当componentType为custom时使用）
 * @param cascaderConfig - 级联选择器配置（当componentType为cascader时使用）
 */
export interface SearchItemConfig {
  key: string
  label: string
  componentType: ComponentType
  placeholder?: string
  options?: SearchOption[] | CascaderOption[]
  getOptions?: () => Promise<SearchOption[] | CascaderOption[]> | SearchOption[] | CascaderOption[]
  selectMode?: SelectMode
  props?: Record<string, any>
  render?: (props: any) => React.ReactNode
  style?: React.CSSProperties
  cascaderConfig?: CascaderConfig
}

/**
 * 搜索过滤器组件属性接口
 * @param onSearch - 搜索执行回调，接收完整的搜索参数
 * @param searchItems - 搜索项配置数组
 * @param defaultSearchType - 默认搜索类型
 * @param defaultSearchValue - 默认搜索值
 * @param placeholder - 默认占位符
 * @param style - 自定义样式
 * @param debounceTime - 防抖时间（毫秒），默认300ms
 */
export interface SearchFilterProps {
  onSearch: (params: {
    searchType: string
    searchValue: string | undefined
    cascaderValue: any[]
    filterParams: Record<string, any>
  }) => void
  searchItems: SearchItemConfig[]
  defaultSearchType?: string
  defaultSearchValue?: string
  placeholder?: string
  style?: React.CSSProperties
  debounceTime?: number
}

/**
 * 通用搜索过滤器组件
 * 支持多种搜索控件类型：输入框、下拉选择、级联选择、自定义组件
 * 通过配置驱动，高度抽象化，易于复用和扩展
 * 组件内部管理状态，只需外部提供一个搜索回调函数
 */
const SearchFilter: React.FC<SearchFilterProps> = ({
  onSearch,
  searchItems,
  defaultSearchType,
  defaultSearchValue,
  placeholder = t('请输入搜索内容'),
  style,
  debounceTime = 300,
}) => {
  // 内部状态管理
  const [searchType, setSearchType] = useState(defaultSearchType || searchItems[0]?.key || '')
  const [searchValue, setSearchValue] = useState(defaultSearchValue || undefined)
  const [cascaderValue, setCascaderValue] = useState<any[]>([])
  const [optionsMap, setOptionsMap] = useState<Record<string, SearchOption[] | CascaderOption[]>>(
    {},
  )
  const [loadingMap, setLoadingMap] = useState<Record<string, boolean>>({})

  /**
   * 防抖搜索函数，避免频繁触发搜索
   */
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setSearchValue(value)
      triggerSearch(value)
    }, debounceTime),
    [debounceTime],
  )

  /**
   * 触发搜索，构建完整的搜索参数
   */
  const triggerSearch = useCallback(
    (value?: string | string[]) => {
      const currentValue = value ?? searchValue ?? undefined
      const filterParams: Record<string, any> = {}

      // 根据当前搜索类型设置对应的过滤参数
      if (searchType && currentValue) {
        filterParams[searchType] = currentValue
      }

      onSearch({
        searchType,
        searchValue: currentValue as string,
        cascaderValue,
        filterParams,
      })
    },
    [searchType, searchValue, cascaderValue, onSearch],
  )

  /**
   * 处理搜索类型改变
   */
  const handleSearchTypeChange = (value: string) => {
    setSearchType(value)
    setSearchValue(undefined)
    setCascaderValue([])
    // 切换搜索类型时触发搜索，清空之前的过滤条件
    triggerSearch('')
  }

  /**
   * 处理输入框值变化
   */
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchValue(value)
    debouncedSearch(value)
  }

  /**
   * 处理搜索执行（回车或点击搜索按钮）
   */
  const handleSearch = () => {
    debouncedSearch.cancel()
    triggerSearch()
  }

  /**
   * 处理下拉选择值变化
   */
  const handleSearchSelectChange = (value: string) => {
    setSearchValue(value)
    triggerSearch(value)
  }

  /**
   * 处理级联选择器值变化
   */
  const handleCascaderChange = (value: any) => {
    setCascaderValue(value)

    // 处理级联选择器的特殊逻辑
    const processedValue = processCascaderValue(value)
    console.log('processedValue', processedValue)

    triggerSearch(processedValue)
  }

  /**
   * 处理级联选择器值的逻辑
   * 根据配置决定处理方式
   * @param cascaderConfig.expandAtLevel - 当路径长度等于此值时，取该节点下所有子项的ID
   * @param cascaderConfig.maxLevel - 级联的最大层级数
   * @param cascaderConfig.keepFullPath - 是否保留完整路径并展开为所有叶子节点
   */
  const processCascaderValue = (value: any[]): string[] | any[] => {
    if (!value || value.length === 0) return []

    const currentItem = searchItems.find(item => item.key === searchType)
    if (!currentItem || currentItem.componentType !== 'cascader') return []

    const cascaderConfig = currentItem.cascaderConfig || {}
    const options =
      (optionsMap[currentItem.key] as CascaderOption[]) ||
      (currentItem.options as CascaderOption[]) ||
      []

    // 对于需要保留完整路径的情况（如 attckId），展开所有父级选择为完整路径
    if (cascaderConfig.keepFullPath) {
      return expandFullPaths(value, options)
    }

    const { expandAtLevel = 1, maxLevel } = cascaderConfig

    const processedIds: string[] = []

    value.forEach((selectedPath: any[]) => {
      const pathLength = selectedPath.length

      // 如果配置了最大层级，且路径长度超过最大层级，截取到最大层级
      const actualPath =
        maxLevel && pathLength > maxLevel ? selectedPath.slice(0, maxLevel) : selectedPath

      const actualPathLength = actualPath.length

      if (actualPathLength === expandAtLevel) {
        // 当路径长度等于配置的展开层级时，取该节点下所有子项的ID
        const parentId = actualPath[actualPathLength - 1]
        const childIds = getAllChildIds(options, parentId)
        processedIds.push(...childIds)
      } else if (actualPathLength > expandAtLevel) {
        // 当路径长度大于展开层级时，取最后一个元素（叶子节点）
        processedIds.push(String(actualPath[actualPathLength - 1]))
      } else {
        // 当路径长度小于展开层级时，取当前路径的最后一个元素
        processedIds.push(String(actualPath[actualPathLength - 1]))
      }
    })

    return processedIds
  }

  /**
   * 展开级联选择器的完整路径
   * 将父级选择展开为所有叶子节点的完整路径数组
   */
  const expandFullPaths = (paths: any[], options: CascaderOption[]): any[] => {
    const results: any[] = []

    paths.forEach(path => {
      expandPath(path, options, results)
    })

    return results
  }

  /**
   * 递归展开单个路径
   */
  const expandPath = (
    path: any[],
    options: CascaderOption[],
    results: any[],
    parentPath: any[] = [],
  ) => {
    if (path.length === 0) return

    const currentId = path[0]
    const remainingPath = path.slice(1)
    const currentFullPath = [...parentPath, currentId]

    // 在当前层级查找节点
    const node = options.find(opt => opt.value === currentId)
    if (!node) return

    if (remainingPath.length === 0) {
      // 当前是路径的最后一级
      if (node.children && node.children.length > 0) {
        // 如果有子节点，递归展开所有子节点
        node.children.forEach(child => {
          expandAllChildren(child, currentFullPath, results)
        })
      } else {
        // 如果是叶子节点，直接添加完整路径
        results.push(currentFullPath)
      }
    } else {
      // 还有更深的路径，继续递归
      if (node.children) {
        expandPath(remainingPath, node.children, results, currentFullPath)
      }
    }
  }

  /**
   * 展开节点的所有子节点（递归到叶子节点）
   */
  const expandAllChildren = (node: CascaderOption, parentPath: any[], results: any[]) => {
    const currentPath = [...parentPath, node.value]

    if (node.children && node.children.length > 0) {
      // 如果有子节点，继续递归
      node.children.forEach(child => {
        expandAllChildren(child, currentPath, results)
      })
    } else {
      // 叶子节点，添加完整路径
      results.push(currentPath)
    }
  }

  /**
   * 递归获取指定父节点下所有子节点的id
   */
  const getAllChildIds = (options: CascaderOption[], parentId: string | number): string[] => {
    const childIds: string[] = []

    const findChildren = (items: CascaderOption[]) => {
      items.forEach(item => {
        if (item.value === parentId && item.children) {
          item.children.forEach(child => {
            childIds.push(String(child.value))
            // 递归获取子节点的子节点
            if (child.children) {
              childIds.push(...getAllChildIds([child], child.value))
            }
          })
        } else if (item.children) {
          findChildren(item.children)
        }
      })
    }

    findChildren(options)
    return childIds
  }

  /**
   * 获取搜索项的选项数据
   */
  const getItemOptions = useCallback(async (item: SearchItemConfig) => {
    const { key, options, getOptions } = item

    // 如果有静态选项，直接使用
    if (options) {
      setOptionsMap(prev => ({ ...prev, [key]: options }))
      return options
    }

    // 如果有动态获取函数，调用获取数据
    if (getOptions) {
      setLoadingMap(prev => ({ ...prev, [key]: true }))
      try {
        const result = await getOptions()
        setOptionsMap(prev => ({ ...prev, [key]: result }))
        return result
      } catch (error) {
        console.error(`Failed to get options for ${key}:`, error)
        return []
      } finally {
        setLoadingMap(prev => ({ ...prev, [key]: false }))
      }
    }

    return []
  }, [])

  /**
   * 初始化选项数据
   */
  useEffect(() => {
    searchItems.forEach(item => {
      if (item.getOptions || item.options) {
        getItemOptions(item)
      }
    })
  }, [searchItems])

  useEffect(
    () => () => {
      setSearchValue(undefined)
      setCascaderValue([])
      setOptionsMap({})
      setLoadingMap({})
    },
    [],
  )

  const renderSearchControl = () => {
    const currentItem = searchItems.find(item => item.key === searchType)
    if (!currentItem) return null

    const commonProps = {
      style: { width: 255, marginRight: 10, ...style, ...currentItem?.style },
      placeholder: currentItem.placeholder || placeholder,
      ...currentItem.props,
    }

    // 获取当前项的选项数据
    const currentOptions = optionsMap[currentItem.key] || currentItem.options || []
    const isLoading = loadingMap[currentItem.key]

    switch (currentItem.componentType) {
      case 'input':
        return (
          <Input
            {...commonProps}
            value={searchValue}
            onChange={handleSearchInputChange}
            onPressEnter={handleSearch}
            style={{ height: 32, ...commonProps.style }}
          />
        )

      case 'select': {
        const selectMode = currentItem.selectMode || 'multiple'
        return (
          <Select
            {...commonProps}
            mode={selectMode === 'multiple' ? 'multiple' : undefined}
            value={searchValue}
            onChange={handleSearchSelectChange}
            options={currentOptions as SearchOption[]}
            style={{ height: 32, ...commonProps.style }}
            maxTagCount={selectMode === 'multiple' ? 2 : undefined}
            showSearch
            showArrow
            loading={isLoading}
            filterOption={(input, option) =>
              ((option?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        )
      }

      case 'cascader':
        return (
          <Cascader
            {...commonProps}
            multiple
            value={cascaderValue}
            onChange={handleCascaderChange}
            options={currentOptions as CascaderOption[]}
            style={{ height: 32, ...commonProps.style }}
            maxTagCount={2}
            showSearch
            searchValue={searchValue}
            onSearch={value => setSearchValue(value)}
            loading={isLoading}
          />
        )

      case 'custom':
        return currentItem.render?.({
          searchValue,
          cascaderValue,
          onSearchValueChange: setSearchValue,
          onCascaderValueChange: setCascaderValue,
          onSearch: triggerSearch,
          options: currentOptions,
          loading: isLoading,
          ...commonProps,
        })

      default:
        return null
    }
  }

  const searchTypeOptions = useMemo(
    () =>
      searchItems.map(item => ({
        value: item.key,
        label: item.label,
      })),
    [searchItems],
  )

  return (
    <Wrapper>
      <Select
        virtual={false}
        value={searchType}
        onChange={handleSearchTypeChange}
        options={searchTypeOptions}
        className="search-type-select"
      />

      {renderSearchControl()}
    </Wrapper>
  )
}

export default SearchFilter

const Wrapper = styled.div`
  display: flex;
  align-items: stretch;

  .search-type-select {
    width: 105px;
    margin-right: -1px;

    .ant-select-selector {
      border-radius: 6px 0 0 6px;
    }
  }

  .ant-input,
  .ant-select:not(.search-type-select) {
    width: 255px;
    border-radius: 0 6px 6px 0;
    margin-right: 10px;

    &::placeholder {
      color: #c9cdd4;
    }

    .ant-select-selector {
      border-radius: 0 6px 6px 0;
    }
  }
`
